import { useQuery } from "@tanstack/react-query";
import ApiHttp from "byzantine/src/ApiHttp";

export interface BaiTransactionType {
  code: string;
  description: string | null;
  direction: "credit" | "debit" | "misc";
}

export interface UseBaiTransactionTypesResult {
  types: BaiTransactionType[];
  isLoading: boolean;
  error: string | null;
}

/** Extract a human-readable message from an unknown error value. */
function extractErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  if (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof (error as { message: unknown }).message === "string"
  ) {
    return (error as { message: string }).message;
  }
  return "An unexpected error occurred";
}

/**
 * Fetches BAI2 transaction types from the API.
 *
 * - Fetches once on mount and caches indefinitely (staleTime: Infinity).
 * - Does not retry on failure — errors are surfaced to the caller.
 * - Returns an empty array while loading or on error.
 */
export const useBaiTransactionTypes = (): UseBaiTransactionTypesResult => {
  const { data, isLoading, error } = useQuery<BaiTransactionType[]>({
    queryKey: ["baiTransactionTypes"],
    queryFn: () =>
      ApiHttp.fetch<BaiTransactionType[]>("reports/bai_transaction_types"),
    staleTime: Infinity,
    retry: false,
  });

  const errorMessage: string | null = error ? extractErrorMessage(error) : null;

  return {
    types: error ? [] : data ?? [],
    isLoading,
    error: errorMessage,
  };
};
